import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ApplicationPathConstants, ERROR_CODES } from '@ddt-web/config';
import * as get from 'lodash/get';

import { AbstractErrorHandlerService } from './abstract-error-handler.service';
import { ErrorHelperService } from './error-helper.service';
import { ErrorPopupService } from './error-popup.service';


/**
 * We don't always want all errors to fire on Portfolio Editor, because sometimes the user might be
 * directed there from other places to fix issues with their portfolios!
 * 
 * If you're not in the portfolio editor, we dutifully pass the error through to the next handler,
 * where it should be handled normally.
 * 
 * If you *are* in the portfolio editor, we check if the error is of a type that we want to display
 * and if so, we pass it to the next handler. If not, we do nothing.
 * 
 */
@Injectable()
export class PortfolioEditorErrorHandlerService extends AbstractErrorHandlerService {
	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(contentService, errorPopupService, tealiumAnalyticsService, errorHelperService);
	}

	public handleError(error): void {
		const isPortfolioEditor = this.errorHelperService.getRouterUrl().includes(ApplicationPathConstants.PORTFOLIO_EDITOR_PATH);

		if (!isPortfolioEditor || isPortfolioEditor && this.isErrorCodeAlwaysHandled(error)) {
			// Carry on to the next handler
			this.nextHandler.handleError(error);
		}
	}

	public isErrorCodeAlwaysHandled(error): boolean {
		let errorCodes = [];
		if (get(error, 'responseJson.errorCodes') && !Array.isArray(error.responseJson.errorCodes)) {
			errorCodes = Object.values(error.responseJson.errorCodes).map(error => error['errorCode']);
		} else {
			errorCodes = get(error, 'responseJson.errorCodes', []);
		}

		// List of error codes that should always trigger error handling, even in Portfolio Editor
		const codesToCheckFor = [
			ERROR_CODES.PORTFOLIO_ANALYSIS_HOLDING_FILTERED,
			ERROR_CODES.MISSING_POSITION_MAPPING,
			ERROR_CODES.PORTFOLIO_NOT_FOUND,
			ERROR_CODES.NOT_FOUND_OR_UNAUTHORIZED
		];

		return codesToCheckFor.some(code => errorCodes?.includes(code));
	}
}
